import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';

import { Button } from 'ui-components/components/button';
import { Input } from 'ui-components/components/input';

import AsModalWithContext from '../../../../../components/as-modal/with-context';
import Global from '../../../../../store/global-selectors';
import { ProductDetailsContext } from '../../../context';
import { useProductDetailsContext, type ProductAlias } from '../../../use-product-details-context';
import { DeletionWarningModal } from './deletion-warning-modal';
import { messages } from './messages';
import { StoreDropdown } from './store-dropdown';
import sharedStyle from './aliases.module.less';

type SellerStore = { storeGuid: string; name: string };

type AliasV3Props = {
  alias: ProductAlias;
  onDeleteAlias: (productAliasId: string) => void;
};

const noop = () => undefined;

export const AliasV3 = ({ alias, onDeleteAlias }: AliasV3Props) => {
  const intl = useIntl();
  const { productAliases, setProductAliases } = useProductDetailsContext();
  const { aliasSKU, productAliasId, storeGuid, isNewAlias } = alias;
  const stores = (useSelector(Global.selectSellerStores) ?? []) as SellerStore[];
  const selectedStore = stores.find((store) => store.storeGuid === storeGuid);

  const onConfirmDeletion = useCallback(() => {
    onDeleteAlias(productAliasId);
  }, [onDeleteAlias, productAliasId]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const onCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const confirmDeletionIfNeeded = useCallback(() => {
    if (!isNewAlias) {
      setIsModalOpen(true);
      return;
    }
    onDeleteAlias(productAliasId);
  }, [setIsModalOpen, isNewAlias, onDeleteAlias, productAliasId]);

  const updateAlias = useCallback(
    (newAlias: ProductAlias) => {
      const index = productAliases.findIndex((a) => a.productAliasId === newAlias.productAliasId);
      const updated =
        index !== -1 ? [...productAliases.slice(0, index), newAlias, ...productAliases.slice(index + 1)] : [...productAliases, newAlias];
      setProductAliases(updated);
    },
    [productAliases, setProductAliases],
  );

  const setNewAliasSku = useCallback(
    (sku: string) => {
      updateAlias({ ...alias, aliasSKU: sku });
    },
    [alias, updateAlias],
  );

  const onAliasStoreChange = useCallback(
    (selectedStoreGuid: string) => {
      updateAlias({ ...alias, storeGuid: selectedStoreGuid });
    },
    [alias, updateAlias],
  );

  return (
    <div className={sharedStyle['alias-row']}>
      {!isNewAlias && <Input className={sharedStyle['alias-input']} onChange={noop} readOnly value={aliasSKU} />}
      {isNewAlias && <Input className={sharedStyle['alias-input']} onChange={setNewAliasSku} value={aliasSKU} />}
      <div className={sharedStyle['store-selector']}>
        <StoreDropdown onChange={onAliasStoreChange} selectedStoreId={storeGuid} />
      </div>
      <div className={sharedStyle['alias-delete']}>
        <Button className={sharedStyle['delete-item']} onClick={confirmDeletionIfNeeded} theme='unstyled'>
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </div>
      {isModalOpen && (
        <AsModalWithContext Context={ProductDetailsContext}>
          <DeletionWarningModal
            aliasSku={aliasSKU}
            aliasStore={selectedStore ? selectedStore.name : intl.formatMessage(messages.anyStore)}
            onCloseModal={onCloseModal}
            onConfirmDeletion={onConfirmDeletion}
          />
        </AsModalWithContext>
      )}
    </div>
  );
};
